import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { FaTimes, FaChevronDown, FaChevronRight, FaPalette, FaFont, FaImage, FaSave, FaUndo, FaFileExport, FaFileImport, FaBars } from 'react-icons/fa';
import { FaRedo } from 'react-icons/fa';
import { useEditMode } from '../../context/EditModeContext';
import { useBuilder } from '../../context/BuilderContext';
import {
  useSiteConfig,
  useSiteConfigDispatch,
  useSiteConfigHistory,
  isValidConfig,
  mergeWithDefaults,
  ADMIN_CONFIG_KEY,
} from '../../context/SiteConfigContext';
import { getBlock } from '../../utils/blockRegistry';
import { useDeleteBlock } from '../../hooks/useDeleteBlock';
import { useToast } from '../Toast/ToastContext';
import { useT } from '../../hooks/useT';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import EditorSheet from '../EditorSheet/EditorSheet';
import FieldRenderer from './FieldRenderer';
import SpacingField from './fields/SpacingField';
import ColorField from './fields/ColorField';
import SelectField from './fields/SelectField';
import styles from './PropertyPanel.module.css';

// dnd-kit is only needed while the Outline tab is actually open, so it is
// kept out of the main bundle: everyone who never opens the outline never
// downloads it.
const BlockOutline = lazy(() => import('../BlockOutline/BlockOutline'));

const GOOGLE_FONTS = [
  { value: "'Poppins', sans-serif", label: 'Poppins' },
  { value: "'Inter', sans-serif", label: 'Inter' },
  { value: "'Roboto', sans-serif", label: 'Roboto' },
  { value: "'Open Sans', sans-serif", label: 'Open Sans' },
  { value: "'Lato', sans-serif", label: 'Lato' },
  { value: "'Montserrat', sans-serif", label: 'Montserrat' },
  { value: "'Playfair Display', serif", label: 'Playfair Display' },
  { value: "'Merriweather', serif", label: 'Merriweather' },
  { value: "'Source Code Pro', monospace", label: 'Source Code Pro' },
  { value: "'Fira Code', monospace", label: 'Fira Code' },
];

const FONT_DROPDOWN = [
  { value: '', label: 'Inherit (default)' },
  ...GOOGLE_FONTS,
];

// Poppins + Inter are already loaded render-blocking in public/index.html
// (they're the only two the live theme uses). The other eight only need to
// exist so this dropdown can preview them, so they're loaded lazily below,
// once, only while the editor is actually open.
const LAZY_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Source+Code+Pro:wght@400;600&family=Fira+Code&display=swap';
const LAZY_FONT_LINK_ID = 'fgl-editor-preview-fonts';

const COLOR_FIELDS = [
  { key: 'bgPrimary', label: 'Background' },
  { key: 'bgSecondary', label: 'Cards' },
  { key: 'textHeading', label: 'Headings' },
  { key: 'textAccent', label: 'Accent' },
  { key: 'textBody', label: 'Body Text' },
  { key: 'divider', label: 'Divider' },
  { key: 'sidebarBg', label: 'Sidebar' },
];

function findBlockInConfig(config, blockId) {
  for (const [pageKey, pageData] of Object.entries(config.pages)) {
    const blocks = pageData.blocks || [];
    const blockIndex = blocks.findIndex(b => b.id === blockId);
    if (blockIndex !== -1) {
      return { block: blocks[blockIndex], pageKey, blockIndex };
    }
  }
  return null;
}

function CollapsibleSection({ title, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
        <span>{title}</span>
      </button>
      {isOpen && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
}

function PropertyPanel() {
  const { editMode, setEditMode } = useEditMode();
  const { selectedBlockId, deselectBlock, setSidebarPanelOpen, isDirty, markDirty, markClean, currentPage } = useBuilder();
  const config = useSiteConfig();
  const dispatch = useSiteConfigDispatch();
  const { canUndo, canRedo, undo, redo, clearHistory } = useSiteConfigHistory();
  const deleteBlock = useDeleteBlock();
  const { showToast } = useToast();
  const t = useT();
  const [mainTab, setMainTab] = useState('general');
  const [globalSubTab, setGlobalSubTab] = useState('colors');
  const [confirmingReset, setConfirmingReset] = useState(false);

  const isAdmin = editMode === 'admin';

  useEffect(() => {
    if (selectedBlockId) {
      setMainTab('block');
    }
  }, [selectedBlockId]);

  // Marks the session dirty on any config mutation made while the editor is
  // open — but not on the mutation (if any) that merely opening the editor
  // observes, hence the skip-first-run ref. It is reset every time edit mode
  // goes back to 'off', so a config change from outside the editor (e.g. a
  // visitor flipping the language switcher while not editing) never leaks
  // into the next edit session as a false "unsaved changes" flag.
  //
  // Fragile invariant: this assumes editMode flips to non-'off' and a config
  // change never land in the SAME render/effect pass. If a future code path
  // ever did both at once (e.g. an action that both opens the editor and
  // seeds a mutated config in one go), this effect would still be sitting on
  // a fresh (true) skip flag from the prior 'off' render and would swallow
  // that first edit instead of marking it dirty. Not reachable by anything
  // in this file today — entering edit mode and editing are always separate
  // user actions — but keep this in mind if that ever changes.
  const skipNextConfigChange = useRef(true);
  useEffect(() => {
    if (editMode === 'off') {
      skipNextConfigChange.current = true;
      return;
    }
    if (skipNextConfigChange.current) {
      skipNextConfigChange.current = false;
      return;
    }
    markDirty();
  }, [config, editMode, markDirty]);

  // Lazily load the font-picker preview families (see LAZY_FONT_HREF above)
  // once the editor is actually active, instead of shipping them
  // render-blocking to every visitor in public/index.html.
  useEffect(() => {
    if (editMode === 'off') return;
    if (document.getElementById(LAZY_FONT_LINK_ID)) return;

    const link = document.createElement('link');
    link.id = LAZY_FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = LAZY_FONT_HREF;
    document.head.appendChild(link);
  }, [editMode]);

  if (editMode === 'off') return null;

  // === GENERAL TAB HANDLERS ===
  const handleColorChange = (colorKey, value) => {
    dispatch({ type: 'UPDATE_PATH', path: `theme.colors.${colorKey}`, value });
  };

  const handleFontChange = (fontType, fontName) => {
    dispatch({ type: 'UPDATE_PATH', path: `theme.fonts.${fontType}`, value: fontName });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (isAdmin) {
      // Admin: persist to public/images/ folder
      const imagePath = `/images/${file.name}`;
      showToast({
        message: `${t('editor.imageInstructions', 'Add the file to public/images/, then reload to see it.')} (${file.name})`,
      });
      dispatch({ type: 'UPDATE_PATH', path: 'pages.home.profileImage', value: imagePath });
    } else {
      // Visitor: temporary blob URL for this session
      const url = URL.createObjectURL(file);
      dispatch({ type: 'UPDATE_PATH', path: 'pages.home.profileImage', value: url });
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'siteConfig.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      let imported;
      try {
        imported = JSON.parse(event.target.result);
      } catch {
        alert(t('editor.importParseError', 'That file is not valid JSON. Nothing was changed.'));
        return;
      }

      // Validate before touching state or storage: a bad config used to be
      // written to localStorage and only crash afterwards.
      if (!isValidConfig(imported)) {
        alert(
          t(
            'editor.importShapeError',
            'That JSON is not a site config — it needs meta, theme (colors, fonts, sizes), at least one page, i18n.es and sidebar. Nothing was changed.'
          )
        );
        return;
      }

      const next = mergeWithDefaults(imported);

      if (isAdmin) {
        try {
          localStorage.setItem(ADMIN_CONFIG_KEY, JSON.stringify(next));
        } catch {
          alert(
            t(
              'editor.storageError',
              'Could not write to browser storage — the config may be too large or storage is blocked. Nothing was changed.'
            )
          );
          return;
        }
      }

      dispatch({ type: 'SET_CONFIG', config: next });
    };
    reader.readAsText(file);
  };

  const handleSave = () => {
    if (isAdmin) {
      try {
        localStorage.setItem(ADMIN_CONFIG_KEY, JSON.stringify(config));
      } catch {
        alert(
          t(
            'editor.storageError',
            'Could not write to browser storage — the config may be too large or storage is blocked. Nothing was changed.'
          )
        );
        return;
      }
    }
    markClean();
    clearHistory();
    deselectBlock();
    setEditMode('off');
  };

  const handleReset = () => {
    setConfirmingReset(true);
  };

  const handleResetConfirmed = () => {
    if (isAdmin) {
      try {
        localStorage.removeItem(ADMIN_CONFIG_KEY);
      } catch {
        // Storage unavailable — the in-memory reset below still applies.
      }
    }
    dispatch({ type: 'RESET' });
    markClean();
    clearHistory();
    deselectBlock();
    setEditMode('off');
  };

  const handleExit = () => {
    clearHistory();
    deselectBlock();
    setEditMode('off');
  };

  // === BLOCK TAB DATA ===
  let block = null, blockDef = null, blockPath = null, pageKey = null;
  if (selectedBlockId) {
    const found = findBlockInConfig(config, selectedBlockId);
    if (found) {
      block = found.block;
      blockDef = getBlock(block.type);
      blockPath = `pages.${found.pageKey}.blocks.${found.blockIndex}`;
      pageKey = found.pageKey;
    }
  }

  const handlePropChange = (key, value) => {
    dispatch({ type: 'UPDATE_PATH', path: `${blockPath}.props.${key}`, value });
  };

  const handleStyleChange = (key, value) => {
    dispatch({ type: 'UPDATE_PATH', path: `${blockPath}.style.${key}`, value });
  };

  const peekBar = (
    <div className={styles.header}>
      <span className={styles.blockLabel}>
        {isAdmin ? t('editor.adminBanner', 'Admin Mode') : t('editor.playgroundBanner', 'Playground')}
      </span>
      <div className={styles.peekActions}>
        <button
          className={styles.peekBtn}
          onClick={undo}
          disabled={!canUndo}
          aria-label={t('editor.undo', 'Undo')}
          title={t('editor.undo', 'Undo')}
        >
          <FaUndo />
        </button>
        <button
          className={styles.peekBtn}
          onClick={redo}
          disabled={!canRedo}
          aria-label={t('editor.redo', 'Redo')}
          title={t('editor.redo', 'Redo')}
        >
          <FaRedo />
        </button>
        <button
          className={`${styles.peekBtn} ${isDirty ? styles.peekBtnDirty : ''}`}
          onClick={handleSave}
          aria-label={isAdmin ? t('editor.save', 'Save') : t('editor.apply', 'Apply')}
          title={isAdmin ? t('editor.save', 'Save') : t('editor.apply', 'Apply')}
        >
          <FaSave />
        </button>
        <button
          className={styles.peekBtn}
          onClick={handleExit}
          aria-label={t('editor.exit', 'Exit edit mode')}
          title={t('editor.exit', 'Exit edit mode')}
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );

  const tabsBar = (
    <div className={styles.mainTabs}>
      <button
        className={`${styles.mainTab} ${mainTab === 'general' ? styles.mainTabActive : ''}`}
        onClick={() => setMainTab('general')}
      >
        {t('editor.general', 'General')}
      </button>
      <button
        className={`${styles.mainTab} ${mainTab === 'block' ? styles.mainTabActive : ''} ${!selectedBlockId ? styles.mainTabDisabled : ''}`}
        onClick={() => selectedBlockId && setMainTab('block')}
      >
        {selectedBlockId && blockDef ? blockDef.label : t('editor.block', 'Block')}
      </button>
      <button
        className={`${styles.mainTab} ${mainTab === 'outline' ? styles.mainTabActive : ''}`}
        onClick={() => setMainTab('outline')}
      >
        {t('editor.outline', 'Outline')}
      </button>
    </div>
  );

  return (
    <>
    <EditorSheet peekBar={peekBar} tabsBar={tabsBar}>
      {/* ========== GENERAL TAB ========== */}
        {mainTab === 'general' && (
          <>
            {/* Sub-tabs: Colors, Fonts, Images */}
            <div className={styles.globalTabs}>
              <button
                className={`${styles.globalTab} ${globalSubTab === 'colors' ? styles.globalTabActive : ''}`}
                onClick={() => setGlobalSubTab('colors')}
              >
                <FaPalette /> {t('editor.colors', 'Colors')}
              </button>
              <button
                className={`${styles.globalTab} ${globalSubTab === 'fonts' ? styles.globalTabActive : ''}`}
                onClick={() => setGlobalSubTab('fonts')}
              >
                <FaFont /> {t('editor.fonts', 'Fonts')}
              </button>
              <button
                className={`${styles.globalTab} ${globalSubTab === 'images' ? styles.globalTabActive : ''}`}
                onClick={() => setGlobalSubTab('images')}
              >
                <FaImage /> {t('editor.images', 'Images')}
              </button>
            </div>

            <div className={styles.globalPanel}>
              {globalSubTab === 'colors' && (
                <div className={styles.colorGrid}>
                  {COLOR_FIELDS.map(({ key, label }) => (
                    <label key={key} className={styles.colorItem}>
                      <input
                        type="color"
                        value={config.theme.colors[key]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className={styles.colorInputSmall}
                      />
                      <span className={styles.colorLabel}>{label}</span>
                    </label>
                  ))}
                </div>
              )}

              {globalSubTab === 'fonts' && (
                <div className={styles.fontPanel}>
                  <label className={styles.fontSelect}>
                    <span>{t('editor.headingFont', 'Heading Font')}</span>
                    <select
                      className={styles.select}
                      value={config.theme.fonts.heading}
                      onChange={(e) => handleFontChange('heading', e.target.value)}
                    >
                      {GOOGLE_FONTS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.fontSelect}>
                    <span>{t('editor.bodyFont', 'Body Font')}</span>
                    <select
                      className={styles.select}
                      value={config.theme.fonts.body}
                      onChange={(e) => handleFontChange('body', e.target.value)}
                    >
                      {GOOGLE_FONTS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              {globalSubTab === 'images' && (
                <div className={styles.imagePanel}>
                  <label className={styles.imageUploadBtn}>
                    <FaImage />
                    <span>{t('editor.profilePhoto', 'Profile Photo')}</span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
                  </label>
                </div>
              )}
            </div>

            {/* General Actions */}
            <div className={styles.globalActions}>
              <button className={styles.globalActionBtn} onClick={handleSave}>
                <FaSave /> {isAdmin ? t('editor.save', 'Save') : t('editor.apply', 'Apply')}
              </button>
              <button className={`${styles.globalActionBtn} ${styles.globalResetBtn}`} onClick={handleReset}>
                <FaUndo /> {t('editor.reset', 'Reset')}
              </button>
              {isAdmin && (
                <>
                  <button className={styles.globalActionBtn} onClick={handleExport}>
                    <FaFileExport /> {t('editor.export', 'Export')}
                  </button>
                  <label className={`${styles.globalActionBtn} ${styles.globalImportLabel}`}>
                    <FaFileImport /> {t('editor.import', 'Import')}
                    <input type="file" accept=".json" onChange={handleImport} hidden />
                  </label>
                  <button className={styles.globalActionBtn} onClick={() => setSidebarPanelOpen(true)}>
                    <FaBars /> {t('editor.sidebarSettings', 'Sidebar')}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* ========== BLOCK TAB ========== */}
        {mainTab === 'block' && selectedBlockId && block && blockDef && (
          <>
            <CollapsibleSection title={t('editor.layoutStyle', 'Layout & Style')}>
              <SpacingField
                label="Padding"
                values={block.style || {}}
                onChange={handleStyleChange}
                keys={['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']}
              />
              <SpacingField
                label="Margin"
                values={block.style || {}}
                onChange={handleStyleChange}
                keys={['marginTop', 'marginRight', 'marginBottom', 'marginLeft']}
              />
              <ColorField
                label="Background Color"
                value={block.style?.backgroundColor || ''}
                onChange={(v) => handleStyleChange('backgroundColor', v)}
              />
              <ColorField
                label="Text Color"
                value={block.style?.textColor || ''}
                onChange={(v) => handleStyleChange('textColor', v)}
              />
              <SelectField
                label="Font Family"
                value={block.style?.fontFamily || ''}
                onChange={(v) => handleStyleChange('fontFamily', v)}
                options={FONT_DROPDOWN}
              />
              <SpacingField
                label="Size"
                values={block.style || {}}
                onChange={handleStyleChange}
                keys={['fontSize', 'borderRadius', 'maxWidth', 'alignSelf']}
              />
            </CollapsibleSection>

            {blockDef.schema.length > 0 && (
              <CollapsibleSection title={t('editor.content', 'Content')}>
                {blockDef.schema.map((fieldDef) => (
                  <FieldRenderer
                    key={fieldDef.key}
                    fieldDef={fieldDef}
                    value={block.props[fieldDef.key]}
                    onDirectChange={(val) => handlePropChange(fieldDef.key, val)}
                    blockPath={blockPath}
                    dispatch={dispatch}
                    config={config}
                    editMode={editMode}
                  />
                ))}
              </CollapsibleSection>
            )}

            <div className={styles.actions}>
              <button
                className={styles.actionBtn}
                onClick={() => dispatch({ type: 'DUPLICATE_BLOCK', pageKey, blockId: block.id })}
              >
                {t('editor.duplicate', 'Duplicate block')}
              </button>
              <button
                className={`${styles.actionBtn} ${styles.dangerBtn}`}
                onClick={() => deleteBlock(pageKey, block.id)}
              >
                {t('editor.delete', 'Delete block')}
              </button>
            </div>
          </>
        )}

      {mainTab === 'block' && !selectedBlockId && (
        <div className={styles.noBlock}>
          {t('editor.noBlockSelected', 'Tap a block on the page to edit it')}
        </div>
      )}

      {/* ========== OUTLINE TAB ========== */}
      {mainTab === 'outline' && (
        <Suspense fallback={<div className={styles.noBlock}>{t('editor.loading', 'Loading…')}</div>}>
          <BlockOutline pageKey={currentPage} />
        </Suspense>
      )}
    </EditorSheet>
    {confirmingReset && (
      <ConfirmDialog
        title={t('editor.resetTitle', 'Reset everything?')}
        message={t(
          'editor.resetMessage',
          'This discards every change and cannot be undone.'
        )}
        confirmLabel={t('editor.reset', 'Reset')}
        cancelLabel={t('editor.cancel', 'Cancel')}
        onConfirm={() => { setConfirmingReset(false); handleResetConfirmed(); }}
        onCancel={() => setConfirmingReset(false)}
      />
    )}
    </>
  );
}

export default PropertyPanel;
